'use client';

import { useQuery } from '@tanstack/react-query';

import type { ProductDisplayData } from '@eliadmin/core/src/api/products/types/product-types';
import { fetchBestsellers } from '@eliadmin/core/src/components/nt/home/bestsellers-source';
import { ProductTile } from '@eliadmin/core/src/components/nt/product/product-tile';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';

const TILE_SIZES =
  '(min-width: 1392px) 314px, (min-width: 1024px) calc((100vw - 136px) / 4), (min-width: 768px) calc((100vw - 96px) / 3), 260px';

/**
 * The bestseller tiles. The page renders the public list; a logged-in customer gets the same
 * request with the auth token (customer prices, favourites), like the core product carousels.
 */
export function BestsellersProducts({
  locale,
  products,
  className,
}: {
  locale: string;
  products: ProductDisplayData[];
  className?: string;
}) {
  const authToken = useCustomerStore(state => state.me?.authToken);
  const { data: customerProducts } = useQuery({
    queryKey: ['nt-bestsellers', locale, authToken],
    queryFn: () => fetchBestsellers({ locale, authToken }),
    enabled: !!authToken,
    staleTime: 5 * 60 * 1000,
  });
  const items = authToken && customerProducts?.length ? customerProducts : products;

  return (
    <ul className={className}>
      {items.map((product, index) => (
        <li
          key={product.id}
          className={cn(
            'flex w-[260px] shrink-0 snap-start md:w-auto',
            index === 0 && 'lg:col-start-3',
          )}
        >
          <ProductTile product={product} imageSizes={TILE_SIZES} />
        </li>
      ))}
    </ul>
  );
}
